import { FC, useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { get } from 'lodash';
import arcadeApiClient from '../../apiClients/arcadeApiClient';
import { Routes, GameResponse, GameFilters, SortDirection } from '../../types';
import { GamesPage } from '../../components/GamesPage';
import { normalizeGame } from './normalizers';
import { usePagination } from '../../hooks';
import { SpectatorViewContext } from '../../providers';
const fetcher = async (url: string): Promise<any> => await arcadeApiClient.get(url).then(res => ({
  totalGames: Number(res.headers['x-total']),
  games: res.data.games.map((game: GameResponse) => normalizeGame(game))
}));
const joinableFetcher = async (url: string): Promise<any> => await arcadeApiClient.get(url).then(res => ({
  games: res.data.games.map(game => ({
    ...game
  }))
}));
export const GamesPageContainer: FC = () => {
  const router = useRouter();
  const {
    query
  } = router;
  const pagination = usePagination({
    page: 0,
    size: 6
  });
  const {
    assumedUser,
    assumedUserFilter
  } = useContext(SpectatorViewContext);
  const gotoHandled = useRef(false);
  const gotoParam = query.goto as string | undefined;
  const isGotoActiveGame = gotoParam === 'active-game';
  const [activeFilter, setActiveFilter] = useState<GameFilters>(() => {
    if (isGotoActiveGame) return GameFilters.Active;
    return get(query, 'status', GameFilters.Active) as GameFilters;
  });
  const {
    sortDirection,
    sortBy
  } = (() => {
    switch (activeFilter) {
      case GameFilters.Upcoming:
        return {
          sortBy: 'starts_at',
          sortDirection: SortDirection.Asc
        };
      case GameFilters.Completed:
        return {
          sortBy: 'expires_at',
          sortDirection: SortDirection.Desc
        };
      case GameFilters.NeedsVerification:
        return {
          sortBy: 'expires_at',
          sortDirection: SortDirection.Desc
        };
      case GameFilters.Active:
      default:
        return {
          sortBy: 'expires_at',
          sortDirection: SortDirection.Asc
        };
    }
  })();
  const {
    data,
    error
  } = useSWR(`/multi_platform/games?${pagination.asParamString}&status=${activeFilter}&sort_by=${sortBy}&sort_direction=${sortDirection}${assumedUserFilter && '&'.concat(assumedUserFilter)}`, fetcher);
  const {
    data: managedData
  } = useSWR(`/multi_platform/games/managed?${pagination.asParamString}&status=${activeFilter}&sort_by=${sortBy}&sort_direction=${sortDirection}${assumedUserFilter && '&'.concat(assumedUserFilter)}`, fetcher);
  const {
    data: joinableData
  } = useSWR(`/multi_platform/games/joinable${assumedUserFilter && '?'.concat(assumedUserFilter)}`, joinableFetcher);
  const isPending = !data && !error;
  useEffect(() => {
    const status = isGotoActiveGame ? GameFilters.Active : get(query, 'status', GameFilters.Active) as GameFilters;
    setActiveFilter(status);
    pagination.resetPage();
  }, [query.status, isGotoActiveGame]);
  useEffect(() => {
    if (gotoParam !== 'active-game' || gotoHandled.current) return;
    if (!data) return; // still loading

    gotoHandled.current = true;
    if (data.games.length > 0) {
      const firstGame = data.games[0];
      router.replace(`${Routes.Games}/${firstGame.id}`);
    } else {
      // No active games — strip the goto param and stay on the page
      const {
        goto,
        ...rest
      } = query;
      router.replace({
        pathname: router.pathname,
        query: rest
      });
    }
  }, [gotoParam, data]);
  const handleFilterChange = async (value: string): Promise<boolean> => await router.push({
    pathname: router.pathname,
    query: {
      status: value
    }
  });
  const handlePageChange = async (page: number): Promise<void> => {
    window.scrollTo(0, 0);
    pagination.setPage(page);
  };
  const handleWinningsClick = async (): Promise<void> => {
    await router.push({
      pathname: Routes.Rewards
    });
  };
  return <GamesPage activeFilter={activeFilter} currentPage={pagination.values.page} games={data?.games || []} managedGames={managedData?.games || []} joinableGames={joinableData?.games} assumedUserName={assumedUser?.name} isPending={isPending} numberOfRows={pagination.values.size} totalManaged={managedData?.totalGames || 0} totalGames={data?.totalGames || 0} onFilterChange={handleFilterChange} onPageChange={handlePageChange} onWinningsClick={handleWinningsClick} data-sentry-element="GamesPage" data-sentry-component="GamesPageContainer" data-sentry-source-file="GamesPageContainer.tsx" />;
};